/**
 * Geometry for the decay-fit comparison chart — EDT/T20/T30/Topt overlaid
 * vs. frequency, so a big spread between them at a given band is itself the
 * signal (a room with a clean single-slope decay has all four methods
 * agree closely; a big EDT-vs-T20 gap flags a non-exponential decay, often
 * audible as "double slope" reverberance).
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, type LinearScale, type LogFreqScale } from './scales';
import { mapSeriesToPlot, type PlotPoint } from './series';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import type { RT60Fits } from './registry';

export type DecayFitMethod = 'edt' | 't20' | 't30' | 'topt';
export const DECAY_FIT_METHODS: DecayFitMethod[] = ['edt', 't20', 't30', 'topt'];

export interface DecayFitSeries {
  method: DecayFitMethod;
  points: PlotPoint[];
}

export interface DecayFitComparisonInput {
  fits: RT60Fits;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface DecayFitComparisonGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  maxVal: number;
  series: DecayFitSeries[];
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeDecayFitComparisonGeometry(opts: DecayFitComparisonInput): DecayFitComparisonGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 200;
  const fMax = opts.fMax ?? 20000;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const { bandHz } = opts.fits;
  if (!bandHz.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, 0, 1, true),
      maxVal: 1, series: [], xTicks, yTicks: [],
    };
  }

  const allVals = DECAY_FIT_METHODS
    .flatMap((m) => opts.fits[m])
    .filter((v) => Number.isFinite(v));
  const hi = allVals.length ? Math.max(...allVals) : 0;
  const maxVal = Math.max(1.0, Math.ceil(Math.max(hi, 0.5) * 1.1 * 10) / 10);
  const yScale = createLinearScale(frame.plotH, 0, maxVal, true);

  const series: DecayFitSeries[] = DECAY_FIT_METHODS.map((method) => ({
    method,
    points: mapSeriesToPlot({ x: bandHz, y: opts.fits[method] }, xScale, yScale),
  }));

  const step = maxVal > 2 ? 0.5 : maxVal > 1 ? 0.25 : 0.1;
  const yTicks: YTick[] = linearTicks(0, maxVal, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: false, label: v.toFixed(2),
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, maxVal, series, xTicks, yTicks };
}
